import { useMsal } from '@azure/msal-react';
import type { AxiosEventMap } from '@shared/axios/axiosEventBus.ts';
import { isProvisioningLockoutErrorCode } from '@shared/errors/index.ts';
import { errorMessageDefinitions } from '@shared/errors/messages.ts';
import axios from 'axios';
import { useCallback, useEffect } from 'react';
import { defineMessages } from 'react-intl';

import { ApiErrorCode } from '@/api/schema/data-contracts.ts';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { parseProblemDetails } from '@/utils/errors';

const REDIRECT_TS_KEY = 'estiworld.auth.last_login_redirect_ts';
const REDIRECT_LOOP_GUARD_MS = 5_000;

export const AxiosErrorHandler = ({
  subscribe,
  unsubscribe,
}: {
  subscribe: (param: keyof AxiosEventMap, handler: (err: unknown) => void) => void;
  unsubscribe: (param: keyof AxiosEventMap, handler: (err: unknown) => void) => void;
}) => {
  const { instance } = useMsal();
  const errorMessages = useMessages(defineMessages<ApiErrorCode>(errorMessageDefinitions));
  const toast = useToast();

  const getFriendlyErrorMessage = useCallback(
    (detail: string | undefined, errorCode: string): string => {
      // Provisioning lockouts must show a clean, localized message and never the backend
      // `detail`, which is an internal diagnostic string (see EWM-669).
      if (errorCode === 'USER_PROVISIONING_MISSING_EMAIL_CLAIM') {
        return errorMessages[errorCode as ApiErrorCode]?.() ?? errorMessages.GENERAL();
      }

      // Always prioritize detail message from backend - it contains the actual error from AI API
      if (detail) {
        // If detail contains specific error information, show it directly
        const detailLower = detail.toLowerCase();

        // Check for authentication/authorization errors - show the detailed message
        if (
          detailLower.includes('unauthorized') ||
          detailLower.includes('authentication failed') ||
          detailLower.includes('api key') ||
          detailLower.includes('401')
        ) {
          // Return the full detail message which includes "OpenAI API authentication failed (401 Unauthorized)..."
          return detail;
        }

        // For other errors, also show the detail (which may contain OpenAI API error details)
        return detail;
      }

      // Fallback to error code message if no detail
      return errorMessages[errorCode as ApiErrorCode]?.() || errorMessages.GENERAL();
    },
    [errorMessages],
  );

  const showErrorMessage = useCallback(
    (error: unknown) => {
      const problemDetails = parseProblemDetails(error);
      const errorMessage = getFriendlyErrorMessage(
        problemDetails.detail as string | undefined,
        (problemDetails.errorCode as string) || 'GENERAL',
      );
      const toastMessage = (
        <div className='flex flex-col'>
          <div>{errorMessage}</div>
          {problemDetails.traceId ? (
            <div className='mt-5 text-xs text-white/70'>Trace ID: {problemDetails.traceId}</div>
          ) : (
            ''
          )}
        </div>
      );
      // EWM-905: a query that retries once (see shouldRetryQuery) fires a separate
      // `responseError` per attempt, each producing an otherwise-identical toast. Keying
      // toastId off the failing request lets react-toastify silently drop the duplicate
      // instead of stacking two toasts for one failure.
      const toastId = axios.isAxiosError(error)
        ? `${error.config?.method ?? ''} ${error.config?.url ?? ''} ${problemDetails.errorCode ?? 'GENERAL'}`
        : undefined;
      toast.showToast(toastMessage, 'error', toastId ? { toastId } : undefined);
    },
    [getFriendlyErrorMessage, toast],
  );

  useEffect(() => {
    const handleResponseError = (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        showErrorMessage(error);
        return;
      }

      // Skip specific error types that should be handled elsewhere
      // Only skip 401 if it's NOT an AI service error (session expiration vs AI API auth failure)
      if (error.status === 401 && error.response?.data?.errorCode !== 'AI_SERVICE_ERROR') {
        void instance
          .handleRedirectPromise()
          .then((result) => {
            if (result) return; // returning from B2C – token is fresh, no redirect needed
            // Guard against a redirect loop when token acquisition keeps failing:
            // if we already redirected within the last 5 s and still get a 401, bail out.
            const last = Number(sessionStorage.getItem(REDIRECT_TS_KEY) ?? 0);
            if (Date.now() - last < REDIRECT_LOOP_GUARD_MS) return;
            sessionStorage.setItem(REDIRECT_TS_KEY, String(Date.now()));
            return instance.loginRedirect();
          })
          .catch((e) => console.error(e));
        return;
      }

      if (error.response?.data?.errorCode === 'VALIDATION') {
        return;
      }

      // Idempotency signal, not a user-facing failure: the "Continue in Detailed Mode"
      // flow re-issues initial-item creation when an estimation is still IN_CREATION but
      // already has items, and recovers in place (EWM-675 follow-up). Surfacing the raw
      // backend message here produced a scary toast on an otherwise-successful action.
      if (
        error.response?.data?.errorCode ===
        ApiErrorCode.CANNOT_CREATE_INITIAL_ESTIMATION_ITEMS_WHEN_ANY_ITEMS_ALREADY_EXIST
      ) {
        return;
      }

      // EWM-1066: a provisioning lockout fails every bootstrap request at once, so toasting it
      // per request stacked ten of them over a splash screen that never resolved. It is terminal
      // and not retryable, so InitializationProvider renders one full-page contact-support state
      // instead - which it does by subscribing to this same event.
      if (isProvisioningLockoutErrorCode(error.response?.data?.errorCode)) {
        return;
      }

      showErrorMessage(error);
    };
    subscribe('responseError', handleResponseError);
    return (): void => unsubscribe('responseError', handleResponseError);
  }, [instance, showErrorMessage, subscribe, unsubscribe]);

  return null;
};
